import { earconScore, type EarconSeverity } from "./earcon.js";
import type { ToneId } from "../i18n/tone/index.js";

export interface AlertSound {
  /** Create/resume the shared AudioContext from a user gesture (autoplay unlock). */
  unlock(): void;
  /** Play the earcon for a severity in the given voice. No-op if audio is unavailable. */
  play(severity: EarconSeverity, voice: ToneId): void;
  /** Play a sample (the warning earcon) — used by the You-tab preview. */
  preview(voice: ToneId): void;
}

type Ctor = new () => AudioContext;

function resolveCtor(): Ctor | null {
  const g = globalThis as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor };
  return g.AudioContext ?? g.webkitAudioContext ?? null;
}

export function createAlertSound(opts: { factory?: () => AudioContext | null } = {}): AlertSound {
  const make = opts.factory ?? ((): AudioContext | null => {
    const C = resolveCtor();
    return C ? new C() : null;
  });
  let ctx: AudioContext | null = null;
  let tried = false;

  const ensure = (): AudioContext | null => {
    if (ctx || tried) return ctx;
    tried = true;
    try { ctx = make(); } catch { ctx = null; }
    return ctx;
  };

  const resumeIfSuspended = (ac: AudioContext): void => {
    if (ac.state === "suspended") {
      try { ac.resume()?.catch(() => {}); } catch { /* blocked: degrade to silent */ }
    }
  };

  const schedule = (severity: EarconSeverity, voice: ToneId): void => {
    const ac = ensure();
    if (!ac) return;
    resumeIfSuspended(ac);
    const t0 = ac.currentTime;
    for (const n of earconScore(severity, voice)) {
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      const filter = ac.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = n.filterHz;
      osc.type = n.wave;
      osc.frequency.value = n.freq;
      osc.detune.value = n.detuneCents;
      const start = t0 + n.startMs / 1000;
      const dur = n.durMs / 1000;
      const atk = n.attackMs / 1000;
      const rel = n.releaseMs / 1000;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(n.gain, start + atk);
      gain.gain.setValueAtTime(n.gain, start + Math.max(atk, dur - rel));
      gain.gain.linearRampToValueAtTime(0, start + dur);
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(ac.destination);
      osc.start(start);
      osc.stop(start + dur + 0.02);
    }
  };

  return {
    unlock() { const ac = ensure(); if (ac) resumeIfSuspended(ac); },
    play(severity, voice) { schedule(severity, voice); },
    preview(voice) { schedule("warning", voice); },
  };
}
